import { FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import classNames from 'classnames';

// components
import FavoritesItemProvider from 'dibs-portfolio-folder/exports/FavoritesItemProvider';
import { Link } from 'dibs-elements/exports/Link';
import { Button } from 'dibs-elements/exports/Button';
import { FormattedMessage } from 'dibs-react-intl';
import LazyImage from 'dibs-react-lazy-image';

import { SharedAuctionPrice } from '../SharedAuctionPrice/SharedAuctionPrice';
import { FavoritesItem } from '../../components/global/FavoriteIcons/FavoritesItem';

// helpers
import { getSrcsetString, getSrcsetUrl } from 'dibs-image-utils/exports/srcSet';
import { trackItemClick, trackItemImpression } from './similarAuctionTracking';

// types
import { SharedRespAuctionItemTile_item$key } from './__generated__/SharedRespAuctionItemTile_item.graphql';
import { SharedRespAuctionItemTile_user$key } from './__generated__/SharedRespAuctionItemTile_user.graphql';

// styles
import styles from './SharedRespAuctionItemTile.scss';

type Props = {
    item: SharedRespAuctionItemTile_item$key;
    user: SharedRespAuctionItemTile_user$key | null;
    favoritesProps: $TSFixMe; // FavoritesProvider still in jsx
    index: number;
    trackingCategory: string;
    currency: string;
    isWhiteBackground?: boolean;
};

const itemFragment = graphql`
    fragment SharedRespAuctionItemTile_item on Item {
        ...FavoritesItem_item
        ...FavoritesItemProvider_item
        ...SharedAuctionPrice_item
        ...similarAuctionTracking_item
        localizedPdpUrl
        title
        isArt
        photos(limit: 1) {
            smallPath
        }
    }
`;

const userFragment = graphql`
    fragment SharedRespAuctionItemTile_user on User {
        ...FavoritesItemProvider_user
        serviceId
    }
`;

export const SharedRespAuctionItemTile: FC<Props> = ({
    item: itemRef,
    user: userRef,
    favoritesProps,
    index,
    trackingCategory,
    currency,
    isWhiteBackground,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);

    const isArt = !!item.isArt;
    const smallPath = item.photos?.[0]?.smallPath;
    const localizedPdpUrl = item.localizedPdpUrl || '';
    const isLargeTile = !(index % 3); // every third tile is large;
    const showSmallImage = isArt || !isLargeTile;
    const imageSize = showSmallImage ? '65%' : '80%';
    return (
        <FavoritesItemProvider item={item} user={user} className={styles.wrapper}>
            {favoritesItemProps => (
                <div
                    className={classNames(styles.item, {
                        [styles.white]: isWhiteBackground,
                    })}
                >
                    <Link
                        href={localizedPdpUrl}
                        className={styles.innerItem}
                        onClick={() =>
                            trackItemClick({
                                index,
                                item,
                                trackingCategory,
                            })
                        }
                    >
                        <div className={styles.favorites}>
                            <FavoritesItem
                                wrapperClass={styles.favoritesWrapper}
                                heartIconWrapperClass={styles.favoritesIconWrapper}
                                item={item}
                                {...favoritesProps}
                                {...favoritesItemProps}
                            />
                        </div>
                        <LazyImage
                            className={styles.imageWrapper}
                            onContentVisible={() =>
                                trackItemImpression({ index, item, trackingCategory })
                            }
                        >
                            <img
                                width={imageSize}
                                height={imageSize}
                                className={styles.itemImage}
                                alt={item.title || ''}
                                srcSet={getSrcsetString(smallPath || '')}
                                src={getSrcsetUrl(smallPath || '', 240)}
                                sizes={'30vw'}
                            />
                        </LazyImage>
                        <SharedAuctionPrice currency={currency} item={item} />
                        <div className={styles.overlay}>
                            {isLargeTile && <div className={styles.title}>{item.title}</div>}

                            <Button size="medium" className={styles.buttonWrapper}>
                                <FormattedMessage
                                    id="SharedRespAuctionItemTile.bidNow"
                                    defaultMessage="Bid Now"
                                />
                            </Button>
                        </div>
                    </Link>
                </div>
            )}
        </FavoritesItemProvider>
    );
};
